import {
  type Accept,
  ErrorCode,
  type FileError,
  type FileRejection,
  type FileWithPath,
} from 'react-dropzone'

import { BYTES_PER_MEGABYTE, PDF_MIME_TYPE } from '@/modules/upload/constants'
import type { FileDropzoneItem } from '@/modules/upload/types'
import { refuseFilesOverPageLimit } from './pageLimit'
import { isFileRejection } from './rejection'

export interface DropzoneRules {
  accept?: Accept
  maxSize?: number
  maxPages?: number
}

const acceptedPatterns = (accept: Accept) =>
  Object.entries(accept).flatMap(([mimeType, extensions]) => [
    mimeType,
    ...[extensions].flat(),
  ])

// Same matching react-dropzone applies (attr-accept): an extension matches the name, a MIME type the type.
const matchesPattern = (file: File, pattern: string) => {
  const expected = pattern.trim().toLowerCase()
  const mimeType = file.type.toLowerCase()
  if (expected.startsWith('.'))
    return file.name.toLowerCase().endsWith(expected)
  if (expected.endsWith('/*')) {
    return mimeType.split('/')[0] === expected.split('/')[0]
  }
  return mimeType === expected
}

const invalidTypeError = (file: File, accept?: Accept): FileError | null => {
  if (!accept) return null
  const patterns = acceptedPatterns(accept)
  if (patterns.some((pattern) => matchesPattern(file, pattern))) return null
  return {
    code: ErrorCode.FileInvalidType,
    message: `File type must be one of ${patterns.join(', ')}`,
  }
}

const fileTooLargeError = (maxBytes: number): FileError => ({
  code: ErrorCode.FileTooLarge,
  message: `File is larger than ${maxBytes} bytes`,
})

const tooLargeError = (file: File, maxSize?: number): FileError | null => {
  if (maxSize === undefined) return null
  const maxBytes = maxSize * BYTES_PER_MEGABYTE
  return file.size <= maxBytes ? null : fileTooLargeError(maxBytes)
}

const rejectionFor = (
  file: FileWithPath,
  rules: DropzoneRules,
): FileRejection | null => {
  const errors = [
    invalidTypeError(file, rules.accept),
    tooLargeError(file, rules.maxSize),
  ].filter((error) => error !== null)
  return errors.length > 0 ? { file, errors } : null
}

// A file built in the browser (a cloud download) has no path; react-dropzone gives every picked file one.
const withPath = (file: File): FileWithPath => {
  if ('path' in file) return file as FileWithPath
  const path = `./${file.name}`
  return Object.assign(file, { path, relativePath: path })
}

const rejectionOrFile = (
  item: FileDropzoneItem,
  rules: DropzoneRules,
): FileRejection | FileWithPath => {
  if (isFileRejection(item)) return item
  const file = withPath(item)
  return rejectionFor(file, rules) ?? file
}

const passPageLimit = (files: FileWithPath[], maxPages?: number) =>
  maxPages === undefined
    ? Promise.resolve(files)
    : refuseFilesOverPageLimit(files, maxPages)

// A cloud file too large to download has no bytes, so it gets its row in the list
// with the same too-large rejection a dropped file gets.
export const refuseTooLargeCloudFile = (
  name: string,
  maxBytes: number,
): FileRejection => ({
  file: withPath(new File([], name, { type: PDF_MIME_TYPE })),
  errors: [fileTooLargeError(maxBytes)],
})

// The one way into the drop zone. Dropped, browsed and cloud files all pass the same type, size and page checks.
// An item already refused is listed as it is.
export const checkIncomingFiles = async (
  items: FileDropzoneItem[],
  rules: DropzoneRules,
): Promise<FileDropzoneItem[]> => {
  const rejections: FileRejection[] = []
  const accepted: FileWithPath[] = []
  for (const item of items) {
    const checked = rejectionOrFile(item, rules)
    if (isFileRejection(checked)) rejections.push(checked)
    else accepted.push(checked)
  }
  return [...rejections, ...(await passPageLimit(accepted, rules.maxPages))]
}
